import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Box } from 'transfix-ui/core'
import { ICarrierState } from 'types/carrier'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { IStore } from 'apps/carrier/utils/reducers'
import { INLINE_MESSAGE_VARIANTS, InlineMessage, Tabs, Tab } from 'transfix-ui'
import { capitalize } from 'utils/capitalize'
import { ARCHIVED_CERTS_LABEL, REQUIRED_INSURANCE_POLICY_TYPES } from '../constants'
import { useInsuranceQuery } from '../graphql/Insurance'
import { IInsurancePolicyFragment } from '../graphql/InsurancePoliciesFragment'
import { getLabel, getPolicyStatus } from '../utils'
import InsuranceTabRow from './InsuranceTabRow'
import InsuranceTabRowArchived from './InsuranceTabRowArchived'
import StatusIcon from './StatusIcon'

const InsuranceTabs = () => {
  const archivedCertsEnabled = useFeatureFlag('highwayArchivedCerts')
  const [tab, setTab] = useState<string>('')

  const { carrierDetails } = useSelector<IStore, ICarrierState>(({ carrier }) => carrier)
  const { id: carrierId = '' } = carrierDetails || {}

  const { data, loading, refetch } = useInsuranceQuery({
    variables: { carrierApplicationId: carrierId },
  })

  const { highwayCarrier } = data || {}
  const { insurancePolicies: highwayPolicies = [] } = highwayCarrier || {}
  const insurancePolicies = highwayPolicies.filter(policy => !!policy) as IInsurancePolicyFragment[]

  const isTypes = insurancePolicies?.map(({ isType }: IInsurancePolicyFragment) => isType)

  const tabLabels = useMemo(() => {
    let tabNames = [...REQUIRED_INSURANCE_POLICY_TYPES, ...isTypes]
    if (archivedCertsEnabled) {
      tabNames = [...tabNames, ARCHIVED_CERTS_LABEL]
    }
    return [...new Set(tabNames)]
  }, [isTypes])

  // Set tab to automobile or first tab when no tab is selected and data is loaded
  useEffect(() => {
    if (!!tab || !tabLabels?.[0] || loading) {
      return
    }

    const automobileTab =
      tabLabels.find(label => label === 'automobile_liability') || tabLabels?.[0]
    const targetTab = getLabel(automobileTab) || capitalize(automobileTab?.replaceAll('_', ' '))

    if (!targetTab) {
      return
    }

    setTab(targetTab)
  }, [tabLabels])

  return (
    <Tabs value={tab} onChange={setTab}>
      {tabLabels.map(label => {
        const formattedLabel = getLabel(label) || capitalize(label?.replaceAll('_', ' ') || '')
        const archivedTab = label === ARCHIVED_CERTS_LABEL
        const policies = insurancePolicies?.filter(({ deletedAt, isType }) =>
          archivedTab ? !!deletedAt : isType === label && !deletedAt
        )
        const policyStatus = policies.reduce(getPolicyStatus, 'inactive')

        return (
          <Tab
            key={label}
            label={formattedLabel}
            icon={<StatusIcon status={policyStatus} isType={label} />}>
            <Box pt={3}>
              {!archivedTab &&
                policies?.map((policy, i) => (
                  <InsuranceTabRow
                    key={policy?.id}
                    insurancePolicy={policy}
                    refetch={refetch}
                    index={i}
                  />
                ))}
              {!archivedTab && !policies?.length && (
                <InlineMessage
                  message={`No ${formattedLabel} insurance on file. Contact Highway Support at service@highway.com.`}
                  type={INLINE_MESSAGE_VARIANTS.WARNING}
                  IconComponent={() => <StatusIcon status={policyStatus} isType={label} />}
                />
              )}
              {archivedCertsEnabled &&
                archivedTab &&
                policies?.map(policy => (
                  <InsuranceTabRowArchived key={policy?.id} insurancePolicy={policy} />
                ))}
            </Box>
          </Tab>
        )
      })}
    </Tabs>
  )
}

export default InsuranceTabs
